/**
 * VIEW AIRPLANES' one screen: a turntable, the name header (the panel's own
 * title), a paragraph of flavor text, and `PREVIOUS` / `NEXT` / `CONTINUE` —
 * all three strings, and the screen's shape, straight from
 * `docs/game/ui-reference/view-airplanes-3d.png` and `PS.EXE`'s string table
 * (`plane-viewer-model.ts`'s header has the full recovery).
 *
 * The turntable well and its four view arrows are the exact geometry
 * `paint-screens.ts` uses for PERSONAL AIRCRAFT — same rationale the mission
 * brief gives: "what the pilot sees … is what takes off" there, and here it's
 * "what the pilot is shown … is what the sim actually flies," so the same
 * rotate/zoom controls over the same kind of well are the right reuse, not a
 * coincidence of layout.
 */

import type { Rect } from "../render/draw-ops.ts";
import type { FontAtlas } from "../render/text-metrics.ts";
import type { MenuAction } from "./menu-model.ts";
import { actionRow, wrapText, type ActionSpec } from "./panel-parts.ts";
import type { ShellScreen, Widget } from "./shell-screen.ts";

const command = (name: string): MenuAction => ({ kind: "command", command: name });

/** The turntable's well — same rect `paint-screens.ts`'s `PREVIEW_RECT` uses for PERSONAL AIRCRAFT. */
export const VIEWER_PREVIEW_RECT: Rect = { x: 20, y: 24, w: 280, h: 106 };

const ARROW_ROW_Y = 136;
const ARROW_W = 22;
const ARROW_H = 16;
const ARROW_X = 18;
const ARROW_PITCH = ARROW_W + 2;

/**
 * The description plate. `y` matches the capture (`view-airplanes-3d.png`'s
 * black text box sits at logical y≈151-171, measured off its own pixels at
 * the capture's 2x scale) — but `h` had been left at the well's own bevel gap
 * (20), too short for two `FONT3` lines (8px glyphs) at `BLURB_PITCH`'s 9px
 * line spacing plus `BLURB_PAD_Y` on both sides: the second line's bottom
 * landed one row past the plate's own bottom bevel, bleeding onto the panel
 * behind it (bug report: "the description text is a tad bit too low and
 * overlaps the panel"). `h: 23` is the smallest height that clears
 * `BLURB_PAD_Y*2 + BLURB_PITCH + fontHeight` on both the plate's inner fill
 * and its outer rect, still with a 1px gap above the arrows row and below
 * before `actionRow`'s own y=178.
 */
export const BLURB_RECT: Rect = { x: 18, y: 153, w: 284, h: 23 };
export const BLURB_PAD_X = 9;
export const BLURB_PAD_Y = 3;
export const BLURB_PITCH = 9;
export const BLURB_MAX_LINES = 2;

function viewArrows(): Widget[] {
  const labels: readonly { readonly id: string; readonly label: string }[] = [
    { id: "rotate-left", label: "<" },
    { id: "rotate-right", label: ">" },
    { id: "zoom-in", label: "+" },
    { id: "zoom-out", label: "-" },
  ];
  return labels.map((entry, index) => ({
    kind: "button",
    id: entry.id,
    rect: { x: ARROW_X + index * ARROW_PITCH, y: ARROW_ROW_Y, w: ARROW_W, h: ARROW_H },
    label: entry.label,
    enabled: true,
    action: command(`view:${entry.id}`),
  }));
}

export interface PlaneViewerScreenSpec {
  readonly title: string;
  readonly blurb: string;
  readonly background: string | undefined;
  /** Disabled (but still focusable, like every other disabled control here) when the roster has one entry. */
  readonly canPage: boolean;
}

/**
 * `font` wraps the blurb, which the renderer draws in FONT3; `heading` measures
 * the action row, which `boardButtonOps` draws in FONT1. The two are separate
 * arguments because the widths differ by 4-13px per label and a button sized
 * off the wrong one prints its label over its own chrome.
 */
export function planeViewerScreen(spec: PlaneViewerScreenSpec, font: FontAtlas, heading: FontAtlas): ShellScreen {
  const actions: ActionSpec[] = [
    { id: "prev", label: "PREVIOUS", action: command("prev"), enabled: spec.canPage },
    { id: "next", label: "NEXT", action: command("next"), enabled: spec.canPage },
    { id: "continue", label: "CONTINUE", action: command("return") },
  ];
  const lines = wrapText(font, spec.blurb, BLURB_RECT.w - 2 * BLURB_PAD_X).slice(0, BLURB_MAX_LINES);
  return {
    id: "planes/viewer",
    chrome: { kind: "panel", title: spec.title, emblem: "none", background: spec.background },
    widgets: [
      { kind: "well", rect: VIEWER_PREVIEW_RECT },
      ...viewArrows(),
      { kind: "plate", rect: BLURB_RECT },
      { kind: "prose", block: { lines, x: BLURB_RECT.x + BLURB_PAD_X, y: BLURB_RECT.y + BLURB_PAD_Y, role: "label", pitch: BLURB_PITCH } },
      ...actionRow(actions, heading, 178),
    ],
    cancel: command("return"),
    viewport: VIEWER_PREVIEW_RECT,
  };
}
